import {
  Component,
  h,
  Element,
  State,
  Prop,
  Watch,
  Method,
} from "@stencil/core"
import {
  AccordionState,
  AccordionActions,
  accordionReducer,
  getInitialState,
} from "./accordion-store"
import {
  StoreConnection,
  StateHandle,
  getAppearance,
} from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-accordion",
  styleUrl: "accordion.scss",
  shadow: true,
})
export class Accordion {
  private store: StoreConnection<this, AccordionState, AccordionActions>

  @Element() public el!: HTMLSdxAccordionElement

  @State() public state!: StateHandle<AccordionState>

  /**
   * Whether multiple accordion items can be open at the same time.
   */
  @Prop() public keepOpen: boolean = false

  /**
   * Position of the arrow (chevron).
   * @private
   */
  @Prop() public arrowPosition: "right" | "left" | "center" = "right"

  /**
   * @private
   */
  @Prop() public componentStyle: Record<string, string | undefined> = {}

  /**
   * Accordion theme.
   * Borderless accordions are meant to be used for more spacious layouts or inside a Card component.
   * See [examples](/samples/accordion.html).
   */
  @Prop() public theme: "borderless" | "" = ""

  @Watch("arrowPosition")
  public arrowPositionChanged() {
    this.store.set("arrowPosition", this.arrowPosition)
  }

  @Watch("keepOpen")
  public keepOpenChanged() {
    this.store.set("keepOpen", this.keepOpen)
  }

  /**
   * Opens a specific accordion item.
   * @param index Index of the accordion item to be opened.
   */
  @Method()
  public async open(index: number) {
    const accordionItemEl = this.state.get().accordionItemEls[index]
    this.store.dispatch({ type: "OPEN_ACCORDION_ITEM_EL", accordionItemEl })
  }

  /**
   * Opens all accordion items.
   */
  @Method()
  public async openAll() {
    this.store.dispatch({ type: "OPEN_ALL_ACCORDION_ITEM_ELS" })
  }

  /**
   * Closes a specific accordion item.
   * @param index Index of the accordion item to be closed.
   */
  @Method()
  public async close(index: number) {
    const accordionItemEl = this.state.get().accordionItemEls[index]
    this.store.dispatch({ type: "CLOSE_ACCORDION_ITEM_EL", accordionItemEl })
  }

  /**
   * Closes all accordion items.
   */
  @Method()
  public async closeAll() {
    this.store.dispatch({ type: "CLOSE_ALL_ACCORDION_ITEM_ELS" })
  }

  /**
   * Toggles a specific accordion item.
   * @param index Index of the accordion item to be toggled.
   */
  @Method()
  public async toggle(index: number) {
    const accordionItemEl = this.state.get().accordionItemEls[index]
    this.store.dispatch({ type: "TOGGLE_ACCORDION_ITEM_EL", accordionItemEl })
  }

  constructor() {
    this.store = new StoreConnection(
      this,
      accordionReducer,
      getInitialState(),
      ["accordionItemEls"],
    )

    this.store.set("arrowPosition", this.arrowPosition)
    this.store.set("theme", this.theme)
    this.store.set("keepOpen", this.keepOpen)

    this.store.flush()
  }

  public componentDidLoad() {
    this.store.subscribe()
  }

  private getComponentClassNames() {
    return {
      component: true,
      [getAppearance(this.el)]: true,
      [this.theme]: true,
    }
  }

  public render() {
    return (
      <div class={this.getComponentClassNames()} style={this.componentStyle}>
        <slot />
      </div>
    )
  }
}
